import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';
import { PageBreakView, LineBreakView, HorizontalLineView } from '../components/LineRuleView';

function insertBlockRuleWithNextParagraph(state, tr, dispatch, nodeTypeName) {
    const { schema, selection } = state;
    const ruleType = schema.nodes[nodeTypeName];
    const paragraphType = schema.nodes.paragraph;
    if (!ruleType || !paragraphType) return false;

    if (!dispatch) return true;

    const ruleNode = ruleType.create();
    const paragraphNode = paragraphType.create();

    // 1. If currently a NodeSelection on an existing block node
    if (selection.node) {
        const after = selection.to;
        tr.insert(after, [ruleNode, paragraphNode]);
        const target = after + ruleNode.nodeSize + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(target, tr.doc.content.size))));
        tr.scrollIntoView();
        return true;
    }

    // 2. If range selected, delete range
    const { from, to } = selection;
    if (from !== to) {
        tr.delete(from, to);
    }

    const $f = tr.doc.resolve(tr.selection.from);
    const parent = $f.parent;

    // 3. If parent paragraph is empty, replace it with [ruleNode, paragraphNode]
    if (parent.isTextblock && parent.content.size === 0 && $f.depth > 0) {
        const pStart = $f.before();
        const pEnd = $f.after();
        tr.replaceWith(pStart, pEnd, [ruleNode, paragraphNode]);
        const target = pStart + ruleNode.nodeSize + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(target, tr.doc.content.size))));
    } else if (parent.isTextblock && $f.parentOffset === parent.content.size && $f.depth > 0) {
        // 4. Cursor is at the end of a paragraph: insert ruleNode and paragraphNode below
        const after = $f.after();
        tr.insert(after, [ruleNode, paragraphNode]);
        const target = after + ruleNode.nodeSize + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(target, tr.doc.content.size))));
    } else if (parent.isTextblock && $f.parentOffset === 0 && $f.depth > 0) {
        // 5. Cursor is at the start of a paragraph: insert ruleNode above, cursor stays in paragraph
        const before = $f.before();
        tr.insert(before, ruleNode);
        const target = before + ruleNode.nodeSize + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(target, tr.doc.content.size))));
    } else if (parent.isTextblock && $f.depth > 0) {
        // 6. Middle of text: split paragraph and insert ruleNode in between
        const curPos = tr.selection.from;
        tr.split(curPos);
        tr.insert(curPos + 1, ruleNode);
        const target = curPos + 1 + ruleNode.nodeSize + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(target, tr.doc.content.size))));
    } else {
        // 7. General fallback
        tr.insert(tr.selection.from, [ruleNode, paragraphNode]);
        const target = tr.selection.from + ruleNode.nodeSize + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(target, tr.doc.content.size))));
    }

    tr.scrollIntoView();
    return true;
}

export const PageBreakBlock = Node.create({
    name: 'pageBreak',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,
    parseHTML() {
        return [
            { tag: 'div[data-type="page-break"]' },
            { tag: 'hr[class*="page-break"]' },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes({ 'data-type': 'page-break', class: 'page-break-block' }, HTMLAttributes)];
    },
    addNodeView() {
        return ReactNodeViewRenderer(PageBreakView);
    },
    addCommands() {
        return {
            setPageBreak: () => ({ state, tr, dispatch }) => {
                return insertBlockRuleWithNextParagraph(state, tr, dispatch, this.name);
            },
        };
    },
});

export const LineBreakBlock = Node.create({
    name: 'lineBreakBlock',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,
    parseHTML() {
        return [
            { tag: 'div[data-type="line-break"]' },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes({ 'data-type': 'line-break', class: 'line-break-block' }, HTMLAttributes)];
    },
    addNodeView() {
        return ReactNodeViewRenderer(LineBreakView);
    },
    addCommands() {
        return {
            setLineBreak: () => ({ state, tr, dispatch }) => {
                return insertBlockRuleWithNextParagraph(state, tr, dispatch, this.name);
            },
        };
    },
});

export const HorizontalLineBlock = Node.create({
    name: 'horizontalLineBlock',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,
    parseHTML() {
        return [
            { tag: 'div[data-type="horizontal-line"]' },
            { tag: 'hr[data-type="horizontal-line"]' },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes({ 'data-type': 'horizontal-line', class: 'horizontal-line-block' }, HTMLAttributes)];
    },
    addNodeView() {
        return ReactNodeViewRenderer(HorizontalLineView);
    },
    addCommands() {
        return {
            setHorizontalLine: () => ({ state, tr, dispatch }) => {
                return insertBlockRuleWithNextParagraph(state, tr, dispatch, this.name);
            },
        };
    },
});
